import { useState, useEffect, FormEvent } from 'react';
import { Pill, Stethoscope, FileText } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { Checkbox } from '~/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Separator } from '~/components/ui/separator';
import { useUpdateStudentProfileMutation, UpdateStudentProfileMutation } from './useUpdateStudentProfileMutation';
import type { VariablesOf } from 'gql.tada';

interface EditStudentProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** StudentProfileFromDetail from query; uses Record<string, any> for test-mock compat */
  studentProfile: Record<string, any>;
  onSuccess?: () => void;
  showMedicalInfo?: boolean;
  showAdminNotes?: boolean;
}

interface FormData {
  medicalCondition: string;
  otcMedicationPermission: boolean | null;
  otcMedicationsApproved: {
    acetaminophen: boolean;
    ibuprofen: boolean;
    coughDrops: boolean;
    other: string | null;
  };
  specialNeeds: string;
  insuranceInfo: {
    carrier: string;
    groupNumber: string;
    policyNumber: string;
    policyHolderName: string;
  };
  adminNotes: string;
  academicProbation: boolean;
  behavioralProbation: boolean;
  pauseAutoReenroll: boolean;
}

export function EditStudentProfileDialog({
  open,
  onOpenChange,
  studentProfile,
  onSuccess,
  showMedicalInfo = true,
  showAdminNotes = true,
}: EditStudentProfileDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    // Medical info
    medicalCondition: '',
    otcMedicationPermission: null,
    otcMedicationsApproved: {
      acetaminophen: false,
      ibuprofen: false,
      coughDrops: false,
      other: null,
    },
    specialNeeds: '',
    insuranceInfo: {
      carrier: '',
      groupNumber: '',
      policyNumber: '',
      policyHolderName: '',
    },
    // Admin metadata
    adminNotes: '',
    academicProbation: false,
    behavioralProbation: false,
    pauseAutoReenroll: false,
  });

  const [updateProfile, { loading }] = useUpdateStudentProfileMutation();

  // Initialize form data from studentProfile
  useEffect(() => {
    if (studentProfile) {
      const meds = studentProfile.medicalInfo?.otcMedicationsApproved;
      const insurance = studentProfile.medicalInfo?.insuranceInfo;
      setFormData({
        medicalCondition: studentProfile.medicalInfo?.medicalCondition || '',
        otcMedicationPermission: studentProfile.medicalInfo?.otcMedicationPermission ?? null,
        otcMedicationsApproved: {
          acetaminophen: meds?.acetaminophen || false,
          ibuprofen: meds?.ibuprofen || false,
          coughDrops: meds?.coughDrops || false,
          other: meds?.other || null,
        },
        specialNeeds: studentProfile.medicalInfo?.specialNeeds || '',
        insuranceInfo: {
          carrier: insurance?.carrier || '',
          groupNumber: insurance?.groupNumber || '',
          policyNumber: insurance?.policyNumber || '',
          policyHolderName: insurance?.policyHolderName || '',
        },
        adminNotes: studentProfile.adminMetadata?.adminNotes || '',
        academicProbation: studentProfile.adminMetadata?.academicProbation || false,
        behavioralProbation: studentProfile.adminMetadata?.behavioralProbation || false,
        pauseAutoReenroll: studentProfile.adminMetadata?.pauseAutoReenroll || false,
      });
    }
  }, [studentProfile, open]);

  const handleOtcPermissionChange = (hasPermission: boolean) => {
    setFormData(prev => ({
      ...prev,
      otcMedicationPermission: hasPermission,
      // If permission is revoked, clear all medication selections
      ...(hasPermission ? {} : {
        otcMedicationsApproved: {
          acetaminophen: false,
          ibuprofen: false,
          coughDrops: false,
          other: null,
        }
      })
    }));
  };

  const handleMedicationToggle = (medication: string, checked: boolean | string | null) => {
    setFormData(prev => ({
      ...prev,
      otcMedicationsApproved: {
        ...prev.otcMedicationsApproved,
        [medication]: checked
      }
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const variables: VariablesOf<typeof UpdateStudentProfileMutation> = {
      studentProfileId: studentProfile.id,
    };

    // Only include medicalInfo if medical section is shown
    if (showMedicalInfo) {
      variables.medicalInfo = {
        medicalCondition: formData.medicalCondition || null,
        otcMedicationPermission: formData.otcMedicationPermission,
        otcMedicationsApproved: formData.otcMedicationPermission ? formData.otcMedicationsApproved : null,
        specialNeeds: formData.specialNeeds || null,
        insuranceInfo: {
          carrier: formData.insuranceInfo.carrier || null,
          groupNumber: formData.insuranceInfo.groupNumber || null,
          policyNumber: formData.insuranceInfo.policyNumber || null,
          policyHolderName: formData.insuranceInfo.policyHolderName || null,
        },
      };
    }

    // Only include adminMetadata if admin section is shown
    if (showAdminNotes) {
      variables.adminMetadata = {
        adminNotes: formData.adminNotes || null,
        academicProbation: formData.academicProbation,
        behavioralProbation: formData.behavioralProbation,
        pauseAutoReenroll: formData.pauseAutoReenroll,
      };
    }

    updateProfile({
      variables,
      onCompleted: (result) => {
        if (result.updateStudentProfile?.studentProfile) {
          onSuccess?.();
          onOpenChange(false);
        }
      },
    });
  };

  const otcPermissionValue = formData.otcMedicationPermission === true ? 'yes'
    : formData.otcMedicationPermission === false ? 'no'
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {showMedicalInfo && showAdminNotes
              ? 'Edit Student Profile'
              : showMedicalInfo
                ? 'Edit Medical Information'
                : 'Edit Admin Notes'}
          </DialogTitle>
          <DialogDescription>
            {showMedicalInfo && showAdminNotes
              ? 'Update medical information and administrative notes.'
              : showMedicalInfo
                ? 'Update medical information for this student.'
                : 'Update administrative notes and flags for this student.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Medical Information Card */}
          {showMedicalInfo && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-5 w-5" />
                Medical Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Medical Condition */}
              <div className="space-y-2">
                <Label htmlFor="medicalCondition">Does the child have a medical condition?</Label>
                <Textarea
                  id="medicalCondition"
                  value={formData.medicalCondition}
                  onChange={(e) => setFormData({ ...formData, medicalCondition: e.target.value })}
                  placeholder="Describe any medical conditions the school should be aware of..."
                  rows={3}
                />
              </div>

              <Separator />

              {/* OTC Medication Permission */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Pill className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-base font-medium">OTC Medication Permission</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Permission for staff to administer over-the-counter medications in case of illness.
                  </p>
                </div>

                <RadioGroup
                  value={otcPermissionValue}
                  onValueChange={(val: string) => handleOtcPermissionChange(val === 'yes')}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="otc-yes" />
                    <Label htmlFor="otc-yes" className="font-normal cursor-pointer">
                      Yes, permission granted
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="otc-no" />
                    <Label htmlFor="otc-no" className="font-normal cursor-pointer">
                      No, contact parent first
                    </Label>
                  </div>
                </RadioGroup>

                {/* Medication Options - Only show when permission is granted */}
                {formData.otcMedicationPermission === true && (
                  <div className="ml-2 space-y-3 p-4 border rounded-lg bg-slate-50/50">
                    <p className="text-sm font-medium text-muted-foreground">
                      Approved medications:
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="med-acetaminophen"
                          checked={formData.otcMedicationsApproved.acetaminophen}
                          onCheckedChange={(checked) => handleMedicationToggle('acetaminophen', checked)}
                        />
                        <Label htmlFor="med-acetaminophen" className="font-normal cursor-pointer">
                          Acetaminophen (Tylenol) or generic
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="med-ibuprofen"
                          checked={formData.otcMedicationsApproved.ibuprofen}
                          onCheckedChange={(checked) => handleMedicationToggle('ibuprofen', checked)}
                        />
                        <Label htmlFor="med-ibuprofen" className="font-normal cursor-pointer">
                          Ibuprofen (Advil) or generic
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="med-cough-drops"
                          checked={formData.otcMedicationsApproved.coughDrops}
                          onCheckedChange={(checked) => handleMedicationToggle('coughDrops', checked)}
                        />
                        <Label htmlFor="med-cough-drops" className="font-normal cursor-pointer">
                          Cough Drops / Throat Lozenges
                        </Label>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="med-other" className="font-normal">
                          Other:
                        </Label>
                        <Input
                          id="med-other"
                          value={formData.otcMedicationsApproved.other || ''}
                          onChange={(e) => handleMedicationToggle('other', e.target.value || null)}
                          placeholder="Additional medications..."
                          className="max-w-md"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Special Needs */}
              <div className="space-y-2">
                <Label htmlFor="specialNeeds">Special Needs / Disabilities</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Any additional information the school should know about special needs or disabilities.
                </p>
                <Textarea
                  id="specialNeeds"
                  value={formData.specialNeeds}
                  onChange={(e) => setFormData({ ...formData, specialNeeds: e.target.value })}
                  placeholder="Describe any special needs or disabilities..."
                  rows={3}
                />
              </div>

              <Separator />

              {/* Insurance Information */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Insurance Information</Label>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="insuranceCarrier">Carrier</Label>
                    <Input
                      id="insuranceCarrier"
                      value={formData.insuranceInfo.carrier}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          insuranceInfo: { ...formData.insuranceInfo, carrier: e.target.value },
                        })
                      }
                      placeholder="Insurance carrier"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="insuranceGroupNumber">Group #</Label>
                    <Input
                      id="insuranceGroupNumber"
                      value={formData.insuranceInfo.groupNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          insuranceInfo: { ...formData.insuranceInfo, groupNumber: e.target.value },
                        })
                      }
                      placeholder="Group number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="insurancePolicyNumber">Insurance Policy # or ID #</Label>
                    <Input
                      id="insurancePolicyNumber"
                      value={formData.insuranceInfo.policyNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          insuranceInfo: { ...formData.insuranceInfo, policyNumber: e.target.value },
                        })
                      }
                      placeholder="Policy number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="insurancePolicyHolderName">Name of Policy Holder or Insured</Label>
                    <Input
                      id="insurancePolicyHolderName"
                      value={formData.insuranceInfo.policyHolderName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          insuranceInfo: { ...formData.insuranceInfo, policyHolderName: e.target.value },
                        })
                      }
                      placeholder="Policy holder name"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Admin Notes Card */}
          {showAdminNotes && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Admin Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="adminNotes">Internal Notes</Label>
                <Textarea
                  id="adminNotes"
                  value={formData.adminNotes}
                  onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                  placeholder="Internal notes about this student..."
                  rows={4}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Probation Flags</h4>
                <p className="text-sm text-muted-foreground">
                  Setting these flags will mark pending enrollments as needing review.
                </p>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="academicProbation"
                    checked={formData.academicProbation}
                    onCheckedChange={(checked) => setFormData({ ...formData, academicProbation: checked as boolean })}
                  />
                  <Label htmlFor="academicProbation" className="font-normal">
                    Academic Probation
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="behavioralProbation"
                    checked={formData.behavioralProbation}
                    onCheckedChange={(checked) => setFormData({ ...formData, behavioralProbation: checked as boolean })}
                  />
                  <Label htmlFor="behavioralProbation" className="font-normal">
                    Behavioral Probation
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pauseAutoReenroll"
                    checked={formData.pauseAutoReenroll}
                    onCheckedChange={(checked) => setFormData({ ...formData, pauseAutoReenroll: checked as boolean })}
                  />
                  <Label htmlFor="pauseAutoReenroll" className="font-normal">
                    Pause Auto Re-enrollment
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditStudentProfileDialog;
